/**
 * Cross-tab poll coordination: only one browser tab hits the gateway at a time.
 * Hidden tabs pause polling entirely.
 */

const STORAGE_KEY = "ead-expui.poll-leader.v1";
const LEADER_TTL_MS = 12_000;
/** Single lane for all gateway IO (health, executions, chat, activity-log, mindmap). */
export const GATEWAY_POLL_LANE = "gateway-io";

type LeaderEntry = { tabId: string; until: number };
type LeaderState = Record<string, LeaderEntry>;

let tabIdCache: string | null = null;

function tabId(): string {
  if (tabIdCache) return tabIdCache;
  try {
    const key = "ead-expui.tab-id.v1";
    const existing = sessionStorage.getItem(key);
    if (existing) {
      tabIdCache = existing;
      return existing;
    }
    const created =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `tab-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    sessionStorage.setItem(key, created);
    tabIdCache = created;
    return created;
  } catch {
    tabIdCache = `tab-${Date.now()}`;
    return tabIdCache;
  }
}

function readState(): LeaderState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as LeaderState;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeState(state: LeaderState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / private mode */
  }
}

/** True when this tab may issue gateway polls (visible + elected leader). */
export function canPollGateway(): boolean {
  return isDocumentVisible() && shouldOwnPollLane(GATEWAY_POLL_LANE);
}

/** @deprecated Use canPollGateway(); lane is ignored — one global leader per browser profile. */
export function shouldOwnPollLane(_lane?: string): boolean {
  if (typeof document !== "undefined" && document.hidden) return false;
  const now = Date.now();
  const id = tabId();
  const state = readState();
  const entry = state[GATEWAY_POLL_LANE];
  if (entry && entry.until > now && entry.tabId !== id) {
    return false;
  }
  state[GATEWAY_POLL_LANE] = { tabId: id, until: now + LEADER_TTL_MS };
  writeState(state);
  return true;
}

export function isDocumentVisible(): boolean {
  return typeof document === "undefined" || !document.hidden;
}
